import { Button } from "@dash/form";
import { Spinner } from "@dash/spinner";
import { useTheme } from "@emotion/react";
import heic2any from "heic2any";
import { PdfViewer } from "@megarax/invoices-invoice-thread";
import { useClientManager } from "@megaron/react-clients";
import type { MessageAttachmentPayloadDto, MessageAttachmentSummaryDto } from "@megaron/support-contracts";
import type { Uuid } from "@megaron/uuid";
import { MdDownload } from "react-icons/md";
import { pdfjs } from "react-pdf";
import { useEffect, useMemo, useState, type KeyboardEvent } from "react";

import { decodeBase64ToUint8Array } from "./supportTicketAttachmentDecode";
import { triggerAttachmentDownload } from "./supportTicketAttachmentTriggerDownload";

if (typeof window !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
}

type Props = {
  ticketId: Uuid;
  messageId: Uuid;
  summary: MessageAttachmentSummaryDto;
  isSelected: boolean;
  onActivate: () => void;
};

export function SupportTicketAttachmentTile({
  ticketId,
  messageId,
  summary,
  isSelected,
  onActivate,
}: Props) {
  const theme = useTheme();
  const attachmentQuery = useClientManager("support").getTicketMessageAttachment().useQuery({
    id: ticketId,
    messageId,
    attachmentId: summary.id,
  });

  const payload: MessageAttachmentPayloadDto | undefined = attachmentQuery.data;
  const displayType = payload?.fileType ?? summary.fileType;

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  const pdfBytes = useMemo(() => {
    if (!payload || displayType !== "application/pdf") return null;
    return decodeBase64ToUint8Array(payload.contentBase64);
  }, [payload, displayType]);

  useEffect(() => {
    if (!payload || displayType === "application/pdf") return undefined;
    if (!displayType.startsWith("image/")) return undefined;
    let urlToRevoke: string | null = null;
    const run = async () => {
      try {
        setImageError(null);
        const raw = decodeBase64ToUint8Array(payload.contentBase64);
        const blobPart = Uint8Array.from(raw);
        if (displayType === "image/heic") {
          const blob = new Blob([blobPart], { type: "image/heic" });
          const converted = await heic2any({ blob, toType: "image/jpeg" });
          const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
          if (!(jpegBlob instanceof Blob)) return;
          const url = URL.createObjectURL(jpegBlob);
          urlToRevoke = url;
          setImageSrc(url);
          return;
        }
        const blob = new Blob([blobPart], { type: displayType });
        const url = URL.createObjectURL(blob);
        urlToRevoke = url;
        setImageSrc(url);
      } catch {
        setImageError("Nie udało się wyświetlić podglądu.");
        setImageSrc(null);
      }
    };
    void run();
    return () => {
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [payload, displayType]);

  const isImage = displayType.startsWith("image/");
  const isPdf = displayType === "application/pdf";

  const noopPreview = () => undefined;

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onActivate();
    }
  };

  return (
    <div
      tabIndex={0}
      onClick={onActivate}
      onKeyDown={handleCardKeyDown}
      aria-label={`Otwórz załącznik ${summary.fileName}`}
      css={{
        flex: "1 1 240px",
        minWidth: "200px",
        maxWidth: "320px",
        borderRadius: theme.smallBorderRadius,
        borderStyle: "solid",
        borderWidth: isSelected ? 2 : 1,
        borderColor: isSelected ? theme.colors.primary : theme.colors.border,
        overflow: "hidden",
        background: theme.colors.background,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        outline: "none",
        ":focus-visible": {
          boxShadow: `0 0 0 2px ${theme.colors.primary}`,
        },
      }}
    >
      <div
        css={{
          display: "flex",
          alignItems: "center",
          gap: "0.25rem",
          borderBottom: `1px solid ${theme.colors.border}`,
        }}
      >
        <div
          css={{
            flex: 1,
            minWidth: 0,
            fontSize: "11px",
            padding: "0.35rem 0.5rem",
            color: theme.colors.secondaryText,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {summary.fileName}
        </div>
        {payload && !attachmentQuery.isLoading && !attachmentQuery.isError && (
          <Button
            variant="text"
            size="small"
            icon={<MdDownload />}
            onClick={(event) => {
              event.stopPropagation();
              triggerAttachmentDownload(payload.contentBase64, payload.fileType, payload.fileName);
            }}
            css={{ flexShrink: 0, marginRight: "0.25rem", padding: "0.2em 0.45em" }}
          />
        )}
      </div>
      <div
        css={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "0.35rem",
          minHeight: "228px",
        }}
      >
        {attachmentQuery.isError && (
          <div css={{ fontSize: "12px", color: theme.colors.danger, padding: "0.5rem" }}>Nie udało się wczytać.</div>
        )}
        {attachmentQuery.isLoading && (
          <div css={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
            <Spinner color={theme.colors.primary} size="24px" />
          </div>
        )}
        {!attachmentQuery.isLoading && isPdf && pdfBytes && (
          <PdfViewer file={pdfBytes} showPreviewFunction={noopPreview} error={null} loading={false} />
        )}
        {!attachmentQuery.isLoading && isPdf && !pdfBytes && (
          <div css={{ fontSize: "12px", padding: "0.5rem" }}>Brak danych PDF.</div>
        )}
        {!attachmentQuery.isLoading && isImage && imageError && (
          <div css={{ fontSize: "12px", color: theme.colors.danger, padding: "0.5rem" }}>{imageError}</div>
        )}
        {!attachmentQuery.isLoading && isImage && imageSrc && (
          <div
            css={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "220px",
              borderRadius: theme.smallBorderRadius,
              background: theme.colors.hover,
            }}
          >
            <img
              alt=""
              src={imageSrc}
              css={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "220px",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                pointerEvents: "none",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
